/**
 * <StudentTutorConversationPage> — la conversation de l'élève avec UN de ses tuteurs (page).
 *
 * L'écran `messenger` d'aujourd'hui, sur le socle de conversation : en tête, le retour, la capsule
 * du tuteur et son nom ; puis le fil (jours, salves, l'élève à droite, le tuteur cité avec sa
 * photo) ; en pied, le composeur. Une conversation est avec UN tuteur : l'élève y arrive par la
 * ligne de ce tuteur sur « Messages », par « Contacter mon tuteur » d'une séance individuelle,
 * ou par une notification. Il n'y a pas de liste de conversations.
 *
 * À parité, la page ne montre ni présence du tuteur, ni « est en train d'écrire », ni accusé de
 * lecture : hors live, la messagerie n'en a pas.
 *
 * Contrôlée : le fil arrive groupé (`groupConversation`, le travail du view-model), avec son
 * chargement, son erreur et sa pagination ; le brouillon, l'envoi et la pièce qui part aussi. Le
 * sélecteur de fichier, l'appareil photo, l'enregistreur et les visionneuses sont des effets de
 * l'écran (`onAttach`, `onOpenAttachment`).
 *
 * Le chargement et l'erreur sont ceux du FIL, pas de la page : l'en-tête et le composeur restent,
 * l'élève sait à qui il écrit et peut écrire pendant que le fil arrive.
 *
 * Le tuteur lit la même page de l'autre côté (`<TutorConversationPage>`) : il passe l'élève à qui
 * il parle (`person`, le rond simple, pas la capsule) à la place du tuteur, et retire l'en-tête
 * (`hideHeader`) : la fiche de l'élève a le sien. Sans ces deux props, la page de l'élève, à l'identique.
 *
 * Sur un grand écran, elle est le volet droit de « Messages » (`<StudentCoachTwoPane>`), sans
 * retour. Seule, elle reste une colonne de lecture centrée (`CONVERSATION_MAX_WIDTH`) : le contenu
 * de l'en-tête, le fil et le composeur tombent à l'aplomb les uns des autres.
 */
import React, { useContext } from "react"
import { SafeAreaView } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { CONVERSATION_HEADER_AVATAR_SIZE, ConversationHeader } from "../../molecules/ConversationHeader"
import { type UIMessageLink } from "../../molecules/MessageBubble"
import { type ComposerAttachSource } from "../../molecules/MessageComposer"
import { SPEAKER_HEAD_SIZE, type UIConversationMessage } from "../../molecules/MessageRow"
import { ConversationPanel } from "../../organisms/ConversationPanel"
import {
    CONVERSATION_MAX_WIDTH,
    type UIConversationDay,
    type UIVoicePlayback,
} from "../../organisms/ConversationThread"
import { ScreenPaintedContext } from "../../templates/ScreenBackground/screenPainted"
import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { type UITutor } from "../../../ports/primary/ui/types"

const TEXTS = {
    /** Le champ d'aujourd'hui, mot pour mot. */
    placeholder: "Écrire un message...",
    // « Sarah L. » finit déjà par son point : la phrase n'en ajoute pas un second.
    emptyMessage: (name: string) => `Écris ton premier message à ${name.replace(/\.$/, "")}.`,
    loadError: "Impossible de charger la conversation.",
}

/** L'interlocuteur quand ce n'est pas un tuteur : un élève, vu par son tuteur. */
type ConversationPerson = {
    /** « Léa B. » — dans l'en-tête, au fil et dans la phrase du fil vide. */
    name: string
    avatarUrl?: string
}

export type StudentTutorConversationPageProps = {
    /** Le tuteur à qui l'élève parle (sa capsule). L'écran de l'élève le passe toujours. */
    tutor?: UITutor
    /**
     * Une autre personne que le tuteur, à sa place : l'élève à qui le tuteur parle (le rond simple).
     * Ignorée quand `tutor` est là.
     */
    person?: ConversationPerson
    /** Pas d'en-tête : la page tient sous celui d'une autre. Défaut `false`. */
    hideHeader?: boolean
    /** Sans en-tête : la hauteur de ce qui est posé au-dessus de la page (`<ConversationPanel>`). */
    keyboardOffset?: number
    /** Sous son nom, dans l'en-tête : ses matières. Absente, le nom est seul. */
    caption?: string
    /** Le retour. ABSENT = pas de flèche (la conversation tient dans un volet). */
    onBack?: () => void

    // ── Le fil ──
    /** Les messages, groupés par jour et par salve par le view-model. */
    days: UIConversationDay[]
    /** Premier chargement du fil : son squelette. L'en-tête et le composeur restent. */
    loading?: boolean
    /** La lecture du fil a échoué. Une chaîne se lit telle quelle ; sinon la phrase par défaut. */
    error?: unknown
    onRetry?: () => void
    /** Il reste des messages plus anciens : « Voir les messages précédents », en tête du fil. */
    hasEarlier?: boolean
    loadingEarlier?: boolean
    onLoadEarlier?: () => void
    /** Remonter près du haut charge seul les messages précédents — le web d'aujourd'hui. Défaut `false`. */
    autoLoadEarlier?: boolean
    /** « Renvoyer » un message qui n'est pas parti. */
    onRetryMessage?: (message: UIConversationMessage) => void
    /** Toucher une photo ou un document : l'écran ouvre sa visionneuse. */
    onOpenAttachment?: (message: UIConversationMessage) => void
    /** Appui long sur un message : l'écran ouvre « Copier ». */
    onMessageLongPress?: (message: UIConversationMessage) => void
    /** Le message vocal en cours d'écoute. */
    voicePlayback?: UIVoicePlayback | null
    onToggleVoice?: (message: UIConversationMessage) => void
    /** Le vocal chargé : se placer dedans (de 0 à 1), changer sa vitesse. */
    onSeekVoice?: (message: UIConversationMessage, ratio: number) => void
    onCycleVoiceRate?: (message: UIConversationMessage) => void
    /** Un lien d'un message touché (adresse web, téléphone, e-mail) : l'écran l'ouvre. */
    onPressLink?: (link: UIMessageLink) => void

    // ── Le composeur ──
    draft: string
    onChangeDraft: (text: string) => void
    onSend: () => void
    /** L'envoi est en cours : le bouton tourne. */
    sending?: boolean
    /** Les sources d'une pièce jointe, dans l'ordre. Absentes : pas de bouton « + ». */
    attachSources?: ComposerAttachSource[]
    onAttach?: (source: ComposerAttachSource) => void
    /** Une pièce part : le « + » tourne. */
    attaching?: boolean
    /** Le champ prêt à l'ouverture — le web d'aujourd'hui. Défaut `false`. */
    autoFocus?: boolean
    testID?: string
}

function errorMessage(error: unknown): string | null {
    if (error == null || error === false) return null
    return typeof error === "string" && error.trim() !== "" ? error : TEXTS.loadError
}

export function StudentTutorConversationPage(props: StudentTutorConversationPageProps) {
    const {
        tutor,
        person,
        hideHeader = false,
        keyboardOffset,
        caption,
        onBack,
        days,
        loading,
        error,
        onRetry,
        hasEarlier,
        loadingEarlier,
        onLoadEarlier,
        autoLoadEarlier,
        onRetryMessage,
        onOpenAttachment,
        onMessageLongPress,
        voicePlayback,
        onToggleVoice,
        onSeekVoice,
        onCycleVoiceRate,
        onPressLink,
        draft,
        onChangeDraft,
        onSend,
        sending,
        attachSources,
        onAttach,
        attaching,
        autoFocus,
        testID,
    } = props
    const painted = useContext(ScreenPaintedContext)
    const name = tutor != null ? tutorShortName(tutor) : (person?.name ?? "")
    const avatarUrl = tutor != null ? tutor.avatarUrl : person?.avatarUrl
    const avatarVariant = tutor != null ? "tutor" : "default"

    return (
        <SafeAreaView style={styles.root(painted)} testID={testID}>
            {!hideHeader && (
                <ConversationHeader
                    name={name}
                    caption={caption}
                    avatar={
                        <Avatar
                            variant={avatarVariant}
                            uri={avatarUrl}
                            name={name}
                            size={CONVERSATION_HEADER_AVATAR_SIZE}
                        />
                    }
                    onBack={onBack}
                    maxContentWidth={CONVERSATION_MAX_WIDTH}
                />
            )}
            <ConversationPanel
                // La page réserve déjà le bas de l'écran (`SafeAreaView`), comme `<PageTemplate>`.
                bottomInset={false}
                keyboardOffset={keyboardOffset}
                thread={{
                    days,
                    interlocutor: {
                        speaker: "human",
                        name,
                        // La capsule dans le fil : la taille de la tête d'un champion, la plus petite du livre.
                        avatar: <Avatar variant={avatarVariant} uri={avatarUrl} name={name} size={SPEAKER_HEAD_SIZE} />,
                    },
                    loading,
                    error: errorMessage(error),
                    onRetryLoad: onRetry,
                    emptyMessage: TEXTS.emptyMessage(tutor != null ? tutor.firstname : name),
                    hasEarlier,
                    loadingEarlier,
                    onLoadEarlier,
                    autoLoadEarlier,
                    onRetryMessage,
                    onOpenAttachment,
                    onMessageLongPress,
                    voicePlayback,
                    onToggleVoice,
                    onSeekVoice,
                    onCycleVoiceRate,
                    onPressLink,
                }}
                composer={{
                    value: draft,
                    onChangeText: onChangeDraft,
                    onSend,
                    placeholder: TEXTS.placeholder,
                    sending,
                    attachSources,
                    onAttach,
                    attaching,
                    autoFocus,
                }}
            />
        </SafeAreaView>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le fond de page, sauf sous une coquille qui a déjà peint l'écran — la règle de `<PageTemplate>`. */
    root: (painted: boolean) => ({
        flex: 1,
        backgroundColor: painted ? "transparent" : theme.colors.surface,
    }),
}))
